import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useChat } from "../../../../../context/ChatContext/ChatContext";
import ChatSettingsSelect, { ChatOption } from "./ChatSelect/ChatSettingsSelect";
import { Button, Message } from "../../../../BP2Components";
import { createNewRoom } from "../ChatScripts";
import { v4 as uuidv4 } from "uuid";
import { setChatSettings } from "../../../../../appRedux/actions/ChatSettings";
import { useDispatch } from "react-redux";
import { ChatRoomInterface } from "../ChatInterfaces";
import IntlMessages from "../../../../../util/IntlMessages";
import { useIntl } from "react-intl";
import "../ChatStyle.less";

interface ChatCreateNewRoomProps {
   onRoomCreated?: (room: ChatRoomInterface) => void;
   onCancel?: () => void;
   preselectedUser?: ChatOption | null;
}

const ChatCreateNewRoom: React.FC<ChatCreateNewRoomProps> = ({
   onRoomCreated,
   onCancel,
   preselectedUser = null,
}) => {
   const user = useSelector((state) => state.user);
   const chatSettings = useSelector((state) => state.chatSettings);
   const { chatData } = useChat();
   const dispatch = useDispatch();
   const intl = useIntl();

   const [selectedUsers, setSelectedUsers] = useState<ChatOption[]>([]);
   const [roomTitle, setRoomTitle] = useState("");

   // if there is a preselected user who we don't have a conversation with, then create a new room with them
   useEffect(() => {
      if (preselectedUser) {
         setSelectedUsers([preselectedUser]);
         // Optionally set a default room title based on preselected user
         const userName = preselectedUser.label.split(" ")[0];
         setRoomTitle(intl.formatMessage({ id: "chat.chatWith" }, { name: userName }));
      }
   }, [preselectedUser]);

   // Prepare users for ChatSettingsSelect
   const availableUsers: ChatOption[] = (chatData?.phpUsers || [])
      .map((phpUser: any) => {
         const userId =
            typeof phpUser.oldId === "string" ? parseInt(phpUser.oldId, 10) : phpUser.oldId;
         return {
            value: userId,
            label: `${phpUser.firstName} ${phpUser.lastName}`,
         };
      })
      .filter((user) => !isNaN(user.value as number));

   const handleCreateRoom = () => {
      try {
         // Get selected user IDs
         const selectedUserIds = selectedUsers.map((userOption) => userOption.value);

         // Combine users
         const allUserIds = [user.oldId, ...selectedUserIds];

         // Remove duplicates and ensure all are numbers
         const uniqueUserIds = Array.from(new Set(allUserIds))
            .filter((id) => id !== undefined && id !== null)
            .map((id) => (typeof id === "string" ? parseInt(id, 10) : id))
            .filter((id) => !isNaN(id as number)) as number[];

         // Sort
         const sortedUserIds = uniqueUserIds.sort((a, b) => a - b);

         // room title rules
         let roomTitleToUse = roomTitle;
         if (!roomTitleToUse && selectedUsers.length > 0) {
            roomTitleToUse = selectedUsers.map((opt) => opt.label.split(" ")[0]).join(", ");
         }
         if (!roomTitleToUse) {
            roomTitleToUse = intl.formatMessage({ id: "chat.newRoomTitle" });
         }

         // Use the createNewRoom function that exists in ChatScripts
         const otherUserIds = sortedUserIds.filter((id) => id !== user.oldId);
         const roomType: "direct" | "private" = otherUserIds.length === 1 ? "direct" : "private";
         const newRoom = createNewRoom(
            uuidv4(),
            roomType,
            roomTitleToUse,
            sortedUserIds,
            user.oldId,
         );

         // Ensure key !null for ChatRoomInterface types
         const roomWithSafeKey: ChatRoomInterface = {
            ...newRoom,
            key: newRoom.key || "",
         };

         // Set new chat as current chat
         dispatch(
            setChatSettings({
               ...chatSettings,
               currentRoom: roomWithSafeKey,
            }),
         );

         // callback if applicable
         if (onRoomCreated) {
            onRoomCreated(roomWithSafeKey);
         }

         setSelectedUsers([]);
         setRoomTitle("");
      } catch (error) {
         console.error("Error creating room: ", error);
         Message.error(intl.formatMessage({ id: "chat.createRoomError" }));
      }
   };

   const handleCancel = () => {
      setSelectedUsers([]);
      setRoomTitle("");
      if (onCancel) {
         onCancel();
      }
   };

   return (
      <div className="create-room-modal">
         <div className="room-title-input">
            <label>
               <IntlMessages id="chat.roomTitle" />
            </label>
            <input
               type="text"
               value={roomTitle}
               onChange={(e) => setRoomTitle(e.target.value)}
               placeholder={intl.formatMessage({ id: "chat.roomTitlePlaceholder" })}
               className="room-title-input-field"
            />
         </div>

         <ChatSettingsSelect
            options={availableUsers}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            handleAdd={handleCreateRoom}
            chatSettings={chatSettings}
            hideCreateButton={true}
         />

         <div className="create-room-actions">
            <Button onClick={handleCancel}>
               <IntlMessages id="chat.cancel" />
            </Button>
            <Button
               onClick={handleCreateRoom}
               buttonType="primary"
               disabled={selectedUsers.length === 0}
            >
               <IntlMessages id="chat.createRoom" />
            </Button>
         </div>
      </div>
   );
};

export default ChatCreateNewRoom;
